import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Megaphone, Plus, Eye, MousePointer, TrendingUp, DollarSign,
  Play, Pause, BarChart3, Loader2, Target, Calendar,
  CheckCircle, XCircle, Clock, Zap, Users, ArrowUpRight
} from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';

const STATUS_CONFIG = {
  pending_payment: { label: 'Awaiting Payment', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  active: { label: 'Active', color: 'bg-green-500/20 text-green-400', icon: Play },
  paused: { label: 'Paused', color: 'bg-orange-500/20 text-orange-400', icon: Pause },
  completed: { label: 'Completed', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle },
  expired: { label: 'Expired', color: 'bg-gray-500/20 text-gray-400', icon: Clock },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  removed: { label: 'Removed', color: 'bg-red-500/20 text-red-400', icon: XCircle },
};

export default function AdvertiserDashboardPage() {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [analyticsRes, campaignsRes] = await Promise.all([
        fetch(`${API}/api/ads/my-analytics`, { headers }),
        fetch(`${API}/api/advertiser/campaigns`, { headers }),
      ]);
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (id) => {
    const res = await fetch(`${API}/api/advertiser/campaigns/${id}/pause`, { method: 'PATCH', headers: getAuthHeaders() });
    if (res.ok) { toast.success('Campaign paused'); fetchData(); }
  };

  const handleResume = async (id) => {
    const res = await fetch(`${API}/api/advertiser/campaigns/${id}/resume`, { method: 'PATCH', headers: getAuthHeaders() });
    if (res.ok) { toast.success('Campaign resumed'); fetchData(); }
  };

  const filtered = activeTab === 'all' ? campaigns : campaigns.filter(c => c.status === activeTab);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen py-8" data-testid="ad-manager-dashboard">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <Megaphone className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Ad Manager</h1>
              <p className="text-sm text-muted-foreground">Create and manage your ad campaigns</p>
            </div>
          </div>
          <Button onClick={() => navigate('/ads/create')} className="bg-orange-500 hover:bg-orange-600" data-testid="create-campaign-btn">
            <Plus className="w-4 h-4 mr-2" /> New Campaign
          </Button>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {[
            { label: 'Total Spent', value: analytics?.total_spent_display || '$0.00', icon: DollarSign, color: 'text-green-400 bg-green-500/20' },
            { label: 'Impressions', value: analytics?.total_impressions?.toLocaleString() || '0', icon: Eye, color: 'text-blue-400 bg-blue-500/20' },
            { label: 'Clicks', value: analytics?.total_clicks?.toLocaleString() || '0', icon: MousePointer, color: 'text-purple-400 bg-purple-500/20' },
            { label: 'CTR', value: `${analytics?.avg_ctr || 0}%`, icon: TrendingUp, color: 'text-orange-400 bg-orange-500/20' },
            { label: 'Active', value: analytics?.active_campaigns || 0, icon: Zap, color: 'text-yellow-400 bg-yellow-500/20' },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <Card key={i} className="p-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stat.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-lg font-bold">{stat.value}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Campaigns */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Campaigns</h2>
            <Badge variant="outline">{campaigns.length} total</Badge>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
            <TabsList className="flex-wrap">
              <TabsTrigger value="all">All ({campaigns.length})</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="pending_payment">Pending</TabsTrigger>
              <TabsTrigger value="paused">Paused</TabsTrigger>
              <TabsTrigger value="expired">Expired</TabsTrigger>
            </TabsList>
          </Tabs>

          {filtered.length === 0 ? (
            <div className="text-center py-12" data-testid="no-campaigns-state">
              <Target className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-medium mb-2">No campaigns yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Create your first ad to start reaching the DMV community</p>
              <Button onClick={() => navigate('/ads/create')} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" /> Create Campaign
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(campaign => {
                const sc = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.pending_payment;
                const StatusIcon = sc.icon;
                const ctr = campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) : 0;
                return (
                  <Card key={campaign.campaign_id} className="p-4 border-border hover:border-border transition-colors" data-testid={`campaign-card-${campaign.campaign_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium truncate">{campaign.title}</h3>
                          <Badge className={`text-xs ${sc.color}`}><StatusIcon className="w-3 h-3 mr-1" />{sc.label}</Badge>
                          {campaign.package_id && <Badge variant="outline" className="text-xs capitalize">{campaign.package_id.replace(/_/g, ' ')}</Badge>}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{(campaign.impressions || 0).toLocaleString()}</span>
                          <span className="flex items-center gap-1"><MousePointer className="w-3 h-3" />{(campaign.clicks || 0).toLocaleString()}</span>
                          <span>CTR: {ctr}%</span>
                          {campaign.budget_cents > 0 && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${(campaign.budget_cents / 100).toFixed(2)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        {campaign.status === 'active' && (
                          <Button variant="outline" size="sm" onClick={() => handlePause(campaign.campaign_id)} data-testid="pause-btn"><Pause className="w-4 h-4" /></Button>
                        )}
                        {campaign.status === 'paused' && (
                          <Button variant="outline" size="sm" onClick={() => handleResume(campaign.campaign_id)} data-testid="resume-btn"><Play className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
